import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { PlayCircle, RotateCw, X, Loader2, Sparkles, Rocket } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { track } from "@/lib/analytics";

/**
 * Persistent banner shown across the app when the current user is the shared
 * demo account.
 *
 * Ties into the First 100 Closers program:
 *   - Live seat counter (fetched from /api/early-adopter/stats)
 *   - "Claim First Closer #N →" CTA routes to /register?ref=<stored ref>
 *   - Referral code planted by /demo?ref=CODE flows through to signup
 *
 * Only renders when `user.is_demo_account === true`.
 */
export default function DemoModeBanner() {
  const { user, logout } = useAuth?.() || { user: null, logout: () => {} };
  const nav = useNavigate();
  const [resetting, setResetting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [seatSnap, setSeatSnap] = useState(null); // {total_first_closers, cap, seats_left, cap_reached}

  // Fetch the live First Closer seat count. Re-runs when `user` becomes
  // available so that on /demo?ref= landings — where the axios auth token
  // is written *after* this component first mounts — the CTA & seats chip
  // still populate once the demo user context is ready. Without this
  // effect being keyed on `user`, the initial fetch may 401 (silently)
  // and the CTA disappears at the most conversion-critical moment.
  useEffect(() => {
    if (!user?.is_demo_account) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/early-adopter/stats`);
        if (!cancelled) setSeatSnap(data);
      } catch {
        // Non-fatal — counter simply doesn't render if the endpoint blips.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.is_demo_account]);

  if (!user?.is_demo_account || dismissed) return null;

  const nextCohort = seatSnap ? Math.min(seatSnap.total_first_closers + 1, seatSnap.cap) : null;
  const seatsLeft = seatSnap?.seats_left ?? null;
  const capReached = !!seatSnap?.cap_reached;

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.post("/demo/reset");
      toast.success("Demo data reset — refresh the page to see the fresh workspace.");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset the demo.");
    } finally {
      setResetting(false);
    }
  };

  const handleClaimSeat = async () => {
    // Preserve any ref code planted by /demo?ref=CODE.
    const ref = (localStorage.getItem("af_ref_code") || "").toUpperCase();
    track("demo_claim_seat_clicked", {
      cohort: nextCohort,
      seats_left: seatsLeft,
      has_ref: !!ref,
    });
    // Sign out of the demo account BEFORE routing to register — otherwise
    // the /register page redirects the still-authenticated demo user back
    // to /v3.
    await logout();
    const params = new URLSearchParams();
    if (ref) params.set("ref", ref);
    const qs = params.toString();
    nav(`/register${qs ? `?${qs}` : ""}`, { replace: true });
  };

  const handleExit = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <div
      data-testid="demo-mode-banner"
      className="w-full bg-gradient-to-r from-amber-100 via-amber-50 to-amber-100 border-b border-amber-300 shadow-inner"
    >
      <div className="mx-auto max-w-[1600px] px-4 md:px-8 py-2 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-2 text-[13px] font-black uppercase tracking-[0.14em] text-amber-900">
          <PlayCircle size={16} className="text-amber-700" />
          Demo mode
        </span>

        {/* Live First Closer counter — only when we have snapshot data */}
        {seatSnap && !capReached && (
          <span
            data-testid="demo-banner-seats-chip"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/90 border border-amber-400 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.12em] text-amber-900 shadow-sm"
          >
            <Sparkles size={11} className="text-amber-600" />
            Only {seatsLeft} First Closer {seatsLeft === 1 ? "seat" : "seats"} left
          </span>
        )}

        <span className="text-[13px] text-amber-900/90 leading-tight hidden md:inline">
          Sandbox with sample deals — nothing charged, no real messages sent.
        </span>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* Primary CTA — Claim First Closer seat */}
          {seatSnap && !capReached && (
            <button
              type="button"
              onClick={handleClaimSeat}
              className="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12px] font-black uppercase tracking-[0.08em] text-white bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 border border-amber-700 shadow-md hover:shadow-lg transition-all"
              data-testid="demo-banner-claim-seat-button"
            >
              <Rocket size={12} className="group-hover:translate-x-0.5 transition-transform" />
              Claim First Closer #{nextCohort}
              <span className="ml-1 text-[10px] font-bold px-1 py-0 rounded bg-amber-200 text-amber-900 normal-case tracking-normal">
                40% off · Life
              </span>
            </button>
          )}
          {capReached && (
            <button
              type="button"
              onClick={handleClaimSeat}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold text-white bg-slate-700 hover:bg-slate-800 border border-slate-800 transition-colors"
              data-testid="demo-banner-claim-seat-button"
            >
              <Rocket size={12} />
              Join the waitlist
            </button>
          )}

          <button
            type="button"
            onClick={handleReset}
            disabled={resetting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold text-amber-900 bg-amber-200 hover:bg-amber-300 border border-amber-400 transition-colors disabled:opacity-60"
            data-testid="demo-banner-reset-button"
          >
            {resetting ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
            {resetting ? "Resetting" : "Reset"}
          </button>
          <button
            type="button"
            onClick={handleExit}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-white/80 transition-colors"
            data-testid="demo-banner-exit-button"
          >
            Exit
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Hide banner"
            className="p-1 rounded-md text-amber-800 hover:bg-amber-200 transition-colors"
            data-testid="demo-banner-dismiss-button"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
